import { Button, Spinner, LoadMoreButton, ButtonDropdown } from "@rio-cloud/rio-uikit";
import { useIntl } from "react-intl";
import FailureForm from "../form/FailureForm";
import { Dialog } from "@rio-cloud/rio-uikit";
import { useFailures } from "../../../../hooks/useFailures";
import { RestrictArea } from "../../../../components/restrictArea";

const FailuresPage = () => {
  const intl = useIntl();
  const {
    failures,
    loading,
    deleteLoading,
    error,
    showForm,
    editingFailure,
    showDeleteDialog,
    failureToDelete,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleFormClose,
    setShowForm,
    setShowDeleteDialog,
    editFailure,
    addFailure,
    userHasAccess,
    totalElements,
    loadMoreFailures,
  } = useFailures();

  if (loading || userHasAccess === null) {
    return (
      <>
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      </>
    );
  }

  if (!userHasAccess) {
    return <RestrictArea showBackButton={false} />;
  }

  if (error) {
    return (
      <>
        <div className="flex justify-center items-center h-64">
          <div className="text-red-600 text-center">
            <p className="text-lg font-semibold mb-2">{intl.formatMessage({ id: "intl-msg:backoffice.failuresList.loadError" })}</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="p-6 max-w-[1000px] mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="text-size-h2">{intl.formatMessage({ id: "intl-msg:backoffice.failuresList.title" })}</div>
          </div>
          <Button
            bsStyle={Button.PRIMARY}
            onClick={() => setShowForm(true)}
            iconName="rioglyph-plus-sign"
            className="bg-teal-600 hover:bg-teal-700"
          >
            {intl.formatMessage({ id: "intl-msg:backoffice.failuresList.newFailure" })}
          </Button>
        </div>

        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
              <thead className="bg-gray-50 dark:bg-neutral-900">
                <tr>
                  <th className="px-6 py-3 text-left font-medium text-gray-500 dark:text-neutral-400">
                    {intl.formatMessage({ id: "intl-msg:backoffice.failuresList.columnTitle" })}
                  </th>
                  <th className="px-6 py-3 text-left font-medium text-gray-500 dark:text-neutral-400">
                    {intl.formatMessage({ id: "intl-msg:backoffice.failuresList.columnDescription" })}
                  </th>
                  <th className="px-6 py-3 text-left font-medium text-gray-500 dark:text-neutral-400 w-[120px]">
                    {intl.formatMessage({ id: "intl-msg:backoffice.failuresList.columnVideoLink" })}
                  </th>
                  <th className="px-6 py-3 text-right font-medium text-gray-500 dark:text-neutral-400 w-[100px]"></th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-neutral-800 divide-y divide-gray-200 dark:divide-neutral-700">
                {failures.map((failure) => (
                  <tr key={failure.id} className="hover:bg-gray-50 dark:hover:bg-neutral-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900 dark:text-neutral-100">{failure.title}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-gray-900 dark:text-neutral-100 max-w-xs truncate">{failure.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {failure.videoUrl ? (
                        <span
                          onClick={() => {
                            window.open(failure.videoUrl, '_blank');
                          }}
                          className="text-color-primary cursor-pointer"
                        >
                          {intl.formatMessage({ id: "intl-msg:backoffice.failuresList.watchVideo" })}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-neutral-500">{intl.formatMessage({ id: "intl-msg:backoffice.failuresList.noVideo" })}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <ButtonDropdown
                          title={<span className='rioglyph rioglyph-option-vertical' />}
                          items={[
                            {
                              value: intl.formatMessage({ id: "intl-msg:backoffice.failuresList.edit" }),
                              onSelect: () => handleEdit(failure)
                            },
                            {
                              value: intl.formatMessage({ id: "intl-msg:backoffice.failuresList.delete" }),
                              onSelect: () => handleDelete(failure)
                            }
                          ]}
                          bsStyle='muted'
                          pullRight
                          noCaret
                          usePortal
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {failures.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500 dark:text-neutral-400">
                <p className="font-medium mb-2">
                  {intl.formatMessage({ id: "intl-msg:backoffice.failuresList.empty" })}
                </p>
              </div>
            </div>
          )}

        </div>
          {failures.length > 0 && (
            <div className="p-4 border-t border-gray-200 dark:border-neutral-700">
              <LoadMoreButton
                loaded={failures.length}
                total={totalElements}
                onLoadMore={loadMoreFailures}
                loadMoreMessage={intl.formatMessage({ id: "intl-msg:backoffice.failuresList.loadMore" })}
                noMoreMessage={intl.formatMessage({ id: "intl-msg:backoffice.failuresList.allLoaded" })}
              />
            </div>
          )}

        {showForm && (
          <Dialog
            show={showForm}
            onClose={handleFormClose}
            showCloseButton={true}
            title={editingFailure ? intl.formatMessage({ id: "intl-msg:backoffice.failuresList.editFailure" }) : intl.formatMessage({ id: "intl-msg:backoffice.failuresList.newFailure" })}
            bsSize={Dialog.SIZE_MD}
            body={(
              <FailureForm
                failure={editingFailure}
                onSave={(title, description, videoFile) => {
                  if (editingFailure) {
                    return editFailure(editingFailure.id!, title, description, videoFile);
                  } else if (videoFile) {
                    return addFailure(title, description, videoFile);
                  }
                  return Promise.resolve(false);
                }}
                onCancel={handleFormClose}
              />
            )}
          />
        )}

        <Dialog
          show={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          title={intl.formatMessage({ id: "intl-msg:backoffice.failuresList.confirmDeleteTitle" })}
          bsSize={Dialog.SIZE_SM}
        >
          <div className="p-6">
            <p className="text-size-h4 text-gray-700 dark:text-neutral-300 mb-4">
              {intl.formatMessage({ id: "intl-msg:backoffice.failuresList.areYouSure" })}
            </p>
            <p className="text-gray-600 dark:text-neutral-400">
              {intl.formatMessage(
                { id: "intl-msg:backoffice.failuresList.confirmDeleteMessage" },
                {
                  title: failureToDelete?.title,
                  strong: (chunks) => <strong>{chunks}</strong>,
                }
              )}
            </p>
            <p className="text-gray-600 dark:text-neutral-400 mb-6">
              {intl.formatMessage({ id: "intl-msg:backoffice.failuresList.cannotBeUndone" })}
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                bsStyle={Button.DEFAULT}
                onClick={() => setShowDeleteDialog(false)}
              >
                {intl.formatMessage({ id: "intl-msg:backoffice.failuresList.cancel" })}
              </Button>
              <Button
                bsStyle={Button.DANGER}
                onClick={confirmDelete}
                disabled={deleteLoading}
                iconName={deleteLoading ? "rioglyph-spinner" : undefined}
              >
                {deleteLoading ? intl.formatMessage({ id: "intl-msg:backoffice.failuresList.deleting" }) : intl.formatMessage({ id: "intl-msg:backoffice.failuresList.delete" })}
              </Button>
            </div>
          </div>
        </Dialog>
      </div>
    </>
  );
};

export default FailuresPage;
